import { RubyLoader } from '../components/RubyLoader';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Users, DollarSign, TrendingUp, CheckCircle, Copy, ExternalLink,
  Clock, AlertCircle, RefreshCw, Share2
} from 'lucide-react';
import { api } from '../services/apiService';
import { merchantAppUrl, merchantRoute } from '../utils/merchantUrl';

interface AffiliateInfo {
  isAffiliate: boolean;
  status: string;
  referralCode: string;
  referralLink: string;
  totalReferrals: number;
  conversions: number;
  totalEarnings: number;
  pendingEarnings: number;
}

const TenantAffiliatePanel: React.FC = () => {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [loading, setLoading] = useState(true);
  const [affiliateInfo, setAffiliateInfo] = useState<AffiliateInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const fetchAffiliateInfo = async () => {
    if (!tenantId) return;
    try {
      setLoading(true);
      setError(null);
      const response = await api.affiliates.tenant.getAffiliateInfo(tenantId!);
      setAffiliateInfo(response as AffiliateInfo);
    } catch (err: any) {
      if (err?.message?.includes('404') || err?.message?.includes('not found')) {
        setAffiliateInfo(null);
      } else {
        setError(err?.message || 'Failed to load affiliate info');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAffiliateInfo();
  }, [tenantId]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferralLink = async () => {
    if (!affiliateInfo?.referralLink) return;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join Cafe Menu Master',
          text: 'Check out Cafe Menu Master - the best menu management platform for cafes!',
          url: affiliateInfo.referralLink,
        });
      } catch (err) {
        copyToClipboard(affiliateInfo.referralLink);
      }
    } else {
      copyToClipboard(affiliateInfo.referralLink);
    }
  };

  if (loading) {
    return (
      <RubyLoader variant="section" className="h-64" />
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
        {error}
        <button onClick={fetchAffiliateInfo} className="ml-2 font-medium underline">
          Retry
        </button>
      </div>
    );
  }

  if (!affiliateInfo || !affiliateInfo.isAffiliate) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <DollarSign className="w-8 h-8 text-amber-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Join Our Affiliate Program</h2>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          Earn commissions by referring other cafes to Cafe Menu Master. Share your unique referral link and earn money for every successful signup!
        </p>
        <a
          href={merchantRoute('/affiliate/register')}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-6 rounded-xl transition-colors"
        >
          Apply to Become an Affiliate <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    );
  }

  if (affiliateInfo.status !== 'active') {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-8">
        <div className={`flex items-center gap-3 p-4 rounded-xl mb-4 ${
          affiliateInfo.status === 'pending' 
            ? 'bg-amber-50 border border-amber-200' 
            : 'bg-red-50 border border-red-200'
        }`}>
          {affiliateInfo.status === 'pending' ? (
            <Clock className="w-6 h-6 text-amber-600" />
          ) : (
            <AlertCircle className="w-6 h-6 text-red-600" />
          )}
          <div>
            <p className={`font-medium ${affiliateInfo.status === 'pending' ? 'text-amber-800' : 'text-red-800'}`}>
              {affiliateInfo.status === 'pending' 
                ? 'Application Pending' 
                : 'Account Suspended'}
            </p>
            <p className={`text-sm ${affiliateInfo.status === 'pending' ? 'text-amber-600' : 'text-red-600'}`}>
              {affiliateInfo.status === 'pending'
                ? 'Your affiliate application is under review. We will notify you once approved.'
                : 'Your affiliate account is currently suspended. Please contact support for assistance.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const referralLink = affiliateInfo.referralLink || `${merchantAppUrl()}/#/signup?ref=${affiliateInfo.referralCode}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900">Affiliate Dashboard</h2>
        <button onClick={fetchAffiliateInfo} className="p-2 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <Users className="w-4 h-4" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{affiliateInfo.totalReferrals}</p>
          <p className="text-xs text-gray-500">Total Referrals</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <CheckCircle className="w-4 h-4" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{affiliateInfo.conversions}</p>
          <p className="text-xs text-gray-500">Conversions</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <DollarSign className="w-4 h-4" />
          </div>
          <p className="text-2xl font-bold text-gray-900">${affiliateInfo.totalEarnings.toFixed(2)}</p>
          <p className="text-xs text-gray-500">Total Earned</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <TrendingUp className="w-4 h-4" />
          </div>
          <p className="text-2xl font-bold text-gray-900">${affiliateInfo.pendingEarnings.toFixed(2)}</p>
          <p className="text-xs text-gray-500">Pending</p>
        </div>
      </div>

      {/* Referral Link Card */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">Your Referral Link</h3>
        <p className="text-sm text-gray-500 mb-4">
          Share this link with other cafe owners. You'll earn a commission for each successful signup!
        </p>
        
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={referralLink}
            className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-700 font-mono"
          />
          <button
            onClick={() => copyToClipboard(referralLink)}
            className={`px-4 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2 transition-colors ${
              copied 
                ? 'bg-green-100 text-green-700' 
                : 'bg-amber-600 hover:bg-amber-700 text-white'
            }`}
          >
            {copied ? <><CheckCircle className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy</>}
          </button>
          <button
            onClick={shareReferralLink}
            className="px-4 py-2.5 rounded-lg font-medium text-sm flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
          <span className="text-sm text-gray-500">Your referral code:</span>
          <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono font-medium">{affiliateInfo.referralCode}</code>
        </div>
      </div>

      {/* Full Dashboard Link */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg">Full Affiliate Dashboard</h3>
            <p className="text-amber-100 text-sm">View detailed stats, referral history, and manage payouts</p>
          </div>
          <a
            href="/#/affiliate/dashboard"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-amber-600 font-semibold px-5 py-2.5 rounded-lg hover:bg-amber-50 transition-colors flex items-center gap-2"
          >
            Open Portal <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

export default TenantAffiliatePanel;
